import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useAdminSidebar } from './AdminSidebarContext';

const publicLinks = [
  { to: '/', label: 'Home', icon: 'fa-sharp fa-solid fa-house' },
  { to: '/events', label: 'Events', icon: 'fa-solid fa-calendar-days' },
  { to: '/media', label: 'Gallery', icon: 'fa-solid fa-images' },
  { to: '/lectures', label: 'Lectures', icon: 'fa-solid fa-chalkboard-user' },
  { to: '/books', label: 'Books', icon: 'fa-solid fa-book' },
  { to: '/donate', label: 'Donate', icon: 'fa-solid fa-hand-holding-medical' },
  { to: '/contact', label: 'Contact', icon: 'fa-sharp fa-solid fa-phone-volume' },
  { to: '/blogs', label: 'Blogs', icon: 'fa-solid fa-newspaper' },
];

const MOBILE_NAV_MAX = 1024;

function formatUserName(fullName, roles) {
  const role = roles?.includes('admin') ? 'admin' : 'member';
  const name = (fullName || '').trim();
  if (!name) return role;
  const parts = name.split(/\s+/);
  // Always show firstname and initial of lastname
  const first = parts[0];
  if (parts.length === 1) return first + ' (' + role + ')';
  const lastInitial = parts[parts.length - 1][0];
  return first + ' ' + lastInitial + ' (' + role + ')';
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { isOpen: adminSidebarOpen, toggle: toggleAdminSidebar, close: closeAdminSidebar } = useAdminSidebar();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > MOBILE_NAV_MAX) {
        setMenuOpen(false);
      }
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    // Only hide body overflow on mobile (sidebar in drawer mode)
    // On desktop, sidebar is persistent and body should be scrollable
    const isMobile = window.innerWidth <= MOBILE_NAV_MAX;
    document.body.style.overflow = (menuOpen || (adminSidebarOpen && isMobile)) ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen, adminSidebarOpen, window.innerWidth]);

  const closeMenu = () => setMenuOpen(false);
  const toggleMenu = () => setMenuOpen((open) => !open);

  const handleLogout = () => {
    logout();
    navigate('/');
    closeMenu();
    closeAdminSidebar();
  };

  const authLinks = user
    ? [
        ...(user.roles?.includes('admin')
          ? [{ to: '/admin', label: 'Admin', icon: 'fa-solid fa-shield' }]
          : []),
      ]
    : [{ to: '/account', label: 'Login / Signup', icon: 'fa-solid fa-user' }];

  const navbarClass = 'navbar' + (menuOpen ? ' is-open' : '');
  const navClass = 'navbar-links' + (menuOpen ? ' is-open' : '');

  return (
    <header className={navbarClass}>
      <div className="navbar-bar">
        <div className="navbar-brand" aria-label="Vedanta Center Charlotte">
          <img
            src="/images/Ramakrishna-Mission-Logo_65.png"
            alt="Ramakrishna Mission logo"
            className="navbar-logo primary-logo"
          />
          <img
            src="/images/vsc_2_50.png"
            alt="Vedanta Center Charlotte logo"
            className="navbar-logo secondary-logo"
          />
        </div>

        <div className="navbar-toggle-wrapper">
          <button
            type="button"
            className="navbar-toggle"
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={menuOpen}
            aria-controls="navbar-menu"
            onClick={toggleMenu}
          >
            <i className={menuOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'} aria-hidden="true" />
          </button>

          {/* Admin hamburger toggle - shown only for admin users */}
          {user?.roles?.includes('admin') && (
            <button
              type="button"
              className={'admin-nav-toggle' + (adminSidebarOpen ? ' is-active' : '')}
              onClick={toggleAdminSidebar}
              aria-label={adminSidebarOpen ? 'Close admin menu' : 'Open admin menu'}
              aria-expanded={adminSidebarOpen}
            >
              <i className={adminSidebarOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'} aria-hidden="true" />
              <span className="admin-toggle-label">Admin</span>
            </button>
          )}
        </div>
      </div>

      <nav id="navbar-menu" className={navClass}>
        {publicLinks.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            className={({ isActive }) => (isActive ? 'active' : '')}
            onClick={closeMenu}
          >
            <i className={link.icon} aria-hidden="true" />
            <span>{link.label}</span>
          </NavLink>
        ))}
        {authLinks.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            className={({ isActive }) => (isActive ? 'active' : '')}
            onClick={closeMenu}
          >
            <i className={link.icon} aria-hidden="true" />
            <span>{link.label}</span>
          </NavLink>
        ))}
        {user && (
          <div className="user-account">
            <div className="user-info">
              <span className="user-name">{formatUserName(user.full_name, user.roles)}</span>
            </div>
            <button type="button" className="navbar-logout" onClick={handleLogout}>
              <i className="fa-solid fa-right-from-bracket" aria-hidden="true" />
              <span>Logout</span>
            </button>
          </div>
        )}
      </nav>
    </header>
  );
}